import React, { useState } from 'react';
import '../profile.scss';
import { Text, Input, Dropdown, CheckBox } from '@ui-kit';

// Поля Patreon, которые обычно проставляет крон (utils/crone.js) по данным кампании. У тестовой
// роли патрона нет и крон их не заполнит, поэтому админ выставляет всё руками.
//
// Значения списков взяты из реальных данных, а не придуманы:
// - статусы — из patrons.json (456 записей, все встречающиеся значения);
// - тиры и их названия — из tierPriorities в utils/crone.js;
// - tariff — единственное значение, которое крон туда пишет ("FREE"), плюс пустое.
const PATRON_STATUSES = [
  { name: 'Не задан', value: '' },
  { name: 'active_patron', value: 'active_patron' },
  { name: 'former_patron', value: 'former_patron' },
  { name: 'declined_patron', value: 'declined_patron' },
];

const CHARGE_STATUSES = [
  { name: 'Не задан', value: '' },
  { name: 'Paid', value: 'Paid' },
  { name: 'Declined', value: 'Declined' },
  { name: 'Refunded', value: 'Refunded' },
  { name: 'Fraud', value: 'Fraud' },
];

// Имена кампаний захардкожены в crone.js (campaignsConfig), а их числовые id лежат в env
// бэкенда и на клиент не отдаются — поэтому id вводится руками, а имя выбирается из списка.
const CAMPAIGNS = [
  { name: 'Не задана', value: '' },
  { name: 'KeyHunter', value: 'KeyHunter' },
  { name: 'KeyHunterPatreonMax', value: 'KeyHunterPatreonMax' },
];

const TARIFFS = [
  { name: 'Не задан', value: '' },
  { name: 'FREE', value: 'FREE' },
];

// id тиров Patreon и что они означают — из tierPriorities в crone.js.
const TIERS = [
  { id: '24590974', name: 'KH Classic, 10$' },
  { id: '28297035', name: 'KH Lite, 3$' },
  { id: '27184728', name: 'KH Ferma (Shut + Adobe), 10$' },
  { id: '24530764', name: 'KH Trial, неделя бесплатно' },
  { id: '4135948', name: 'PM Classic (Делись и получай), 8$' },
  { id: '26803705', name: 'PM Plus (Делись и получай + Canva), 10$' },
  { id: '10438249', name: 'PM Free, без доступа' },
];

// freePeriodFrom/To лежат в БД строкой с миллисекундами, а input type="date" работает с
// YYYY-MM-DD. Пустое значение и "0" означают, что периода нет.
const toDateInput = (value) => {
  const ms = Number(value);
  if (!ms) return '';

  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const fromDateInput = (value) => (value ? String(new Date(value).getTime()) : '0');

// Массив может прийти из БД как JSON-массив, а из формы — как что угодно: подстраховываемся.
const toArray = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }

  return [];
};

// Предустановки доступа — повторяют tierPriorities из utils/crone.js: там по набору тиров
// патрона вычисляется суброль, здесь тот же набор проставляется руками. Пресет заполняет всё,
// что нужно для доступа: суброль (по ней user_access выдаёт галочки shutter/adobe/canva), тир и
// статус оплаты, который проверяет ProductService.getData.
//
// isFreeTrial у KH Trial включён не для красоты: в кроне суброль KHTRIAL выставляется ТОЛЬКО
// при активном is_free_trial, а KHCLASSIC/KHLITE с этим флагом наоборот понижаются до KHTRIAL.
// Пресеты повторяют это правило, чтобы ручная настройка не расходилась с автоматической.
const ACCESS_PRESETS = [
  { name: 'Не применять', value: '' },
  { name: 'Tester — доступ по суброли TESTER', value: 'TESTER', subRole: 'TESTER', tiers: [] },
  { name: 'KH Classic, 10$', value: 'KHCLASSIC', subRole: 'KHCLASSIC', tiers: ['24590974'] },
  { name: 'KH Lite, 3$', value: 'KHLITE', subRole: 'KHLITE', tiers: ['28297035'] },
  { name: 'KH Ferma (Shut + Adobe), 10$', value: 'KHFERMA', subRole: 'KHFERMA', tiers: ['27184728'] },
  { name: 'KH Trial, неделя', value: 'KHTRIAL', subRole: 'KHTRIAL', tiers: ['24530764'], isFreeTrial: true },
  { name: 'PM Classic, 8$', value: 'PMCLASSIC', subRole: 'PMCLASSIC', tiers: ['4135948'] },
  { name: 'PM Plus (+ Canva), 10$', value: 'PMPLUS', subRole: 'PMPLUS', tiers: ['26803705'] },
  { name: 'PM Free, без доступа', value: 'PMFREE', subRole: 'PMFREE', tiers: ['10438249'] },
  { name: 'Сбросить доступ', value: 'NONE', subRole: '', tiers: [], revoke: true },
];

const ProfilePatreon = ({ formData = {}, setFormData = () => {}, onChangeInput = () => {} }) => {
  // Выбранная предустановка живёт локально: в модели пользователя её нет, но в поле она должна
  // остаться видимой — иначе непонятно, что именно применили.
  const [preset, setPreset] = useState('');

  const setField = (name, value) => setFormData({ ...formData, [name]: value });

  // Пресет пишется ОДНИМ setFormData: по отдельности каждый вызов брал бы устаревший formData
  // из замыкания и затирал предыдущие поля.
  const applyPreset = (item) => {
    if (!item?.value) return;

    setPreset(item.value);

    setFormData({
      ...formData,
      subRole: item.subRole,
      idTier: item.tiers,
      patronStatus: item.revoke ? '' : 'active_patron',
      lastChargeStatus: item.revoke ? '' : 'Paid',
      isFreeTrial: !!item.isFreeTrial,
      isGifted: false,
      // Кампанию не перетираем, если админ уже выбрал свою: пресет про уровень доступа,
      // а не про то, из какой кампании пришёл патрон.
      compaign: formData?.compaign || 'KeyHunter',
    });
  };

  // isActivated и isConfirmAgreement в модели строковые ("0"/"1"), а isGifted и isFreeTrial —
  // булевы. Пишем в том же типе, в каком поле объявлено: иначе после сохранения значение
  // прочитается не так, как ожидает бэкенд.
  const renderCheckbox = (name, label, asString = false) => {
    const checked = asString ? String(formData?.[name]) === '1' : !!formData?.[name];

    return (
      <div className="profile__input mb-20" key={name}>
        <CheckBox
          name={name}
          label={label}
          checked={checked}
          onChange={() => setField(name, asString ? (checked ? '0' : '1') : !checked)}
        />
      </div>
    );
  };

  const renderSelect = (name, label, options) => {
    const current = formData?.[name] ?? '';

    return (
      <div className="profile__input mb-20">
        <label className="dropdown-label">{label}</label>
        <Dropdown
          id={`input-${name}`}
          data={options}
          search={false}
          placeholder={label}
          value={current}
          onChange={onChangeInput}
          renderOption={(item, index) => {
            const classes = ['pr', 'executor__wrapper', 'input-search__item'];
            if (item.value === current) classes.push('active');

            return (
              <div
                className={classes.join(' ')}
                key={`${name}-${index}`}
                onClick={() => setField(name, item.value)}
              >
                <Text as="span">{item.name}</Text>
              </div>
            );
          }}
        />
      </div>
    );
  };

  const renderDate = (name, label) => (
    <div className="profile__input mb-20">
      <label className="dropdown-label">{label}</label>
      <input
        type="date"
        className="profile__date-input"
        value={toDateInput(formData?.[name])}
        onChange={(e) => setField(name, fromDateInput(e.target.value))}
      />
    </div>
  );

  const tiers = toArray(formData?.idTier).map(String);
  const referrals = toArray(formData?.referrals);

  const toggleTier = (id) => setField(
    'idTier',
    tiers.includes(id) ? tiers.filter((item) => item !== id) : [...tiers, id],
  );

  // Реферал — id пользователя, поэтому поле числовое, а дубликаты не добавляем: крон тоже
  // кладёт id только один раз (см. referrals.includes(user.id) в crone.js).
  const addReferral = () => {
    const input = document.getElementById('profile-referral-input');
    const id = Number(input?.value);

    if (!id || referrals.map(Number).includes(id)) return;

    setField('referrals', [...referrals, id]);
    input.value = '';
  };

  const removeReferral = (id) => setField(
    'referrals',
    referrals.filter((item) => Number(item) !== Number(id)),
  );

  return (
    <div className="profile__patreon divider mb-20">
      <Text as="h5" className="mb-12">Patreon fields</Text>
      <div className="profile__input mb-20">
        <label className="dropdown-label">Предустановка доступа</label>
        <Dropdown
          id="input-access-preset"
          data={ACCESS_PRESETS}
          search={false}
          placeholder="Выбрать предустановку"
          value={preset}
          onChange={onChangeInput}
          renderOption={(item, index) => {
            const classes = ['pr', 'executor__wrapper', 'input-search__item'];
            if (item.value === preset) classes.push('active');

            return (
              <div
                className={classes.join(' ')}
                key={`preset-${index}`}
                onClick={() => applyPreset(item)}
              >
                <Text as="span">{item.name}</Text>
              </div>
            );
          }}
        />
      </div>

      <div className="divider mb-20" />

      <div className="profile__input mb-20">
        <Input
          type="text"
          name="campaignId"
          label="Campaign ID"
          placeholder="Только цифры"
          value={formData?.campaignId || ''}
          onChange={onChangeInput}
        />
      </div>

      {renderSelect('compaign', 'Campaign', CAMPAIGNS)}
      {renderSelect('patronStatus', 'Patron status', PATRON_STATUSES)}
      {renderSelect('lastChargeStatus', 'Last charge status', CHARGE_STATUSES)}
      {renderSelect('tariff', 'Tariff', TARIFFS)}

      {renderDate('freePeriodFrom', 'Free period from')}
      {renderDate('freePeriodTo', 'Free period to')}

      <div className="profile__input mb-20">
        <label className="dropdown-label">ID Tier</label>
        <div className="profile__tiers">
          {TIERS.map((tier) => (
            <CheckBox
              key={tier.id}
              name={`tier-${tier.id}`}
              label={`${tier.name} — ${tier.id}`}
              checked={tiers.includes(tier.id)}
              onChange={() => toggleTier(tier.id)}
            />
          ))}
        </div>
      </div>

      <div className="profile__input mb-20">
        <Input
          type="text"
          name="imgUrl"
          label="Image URL"
          placeholder="Ссылка на картинку"
          value={formData?.imgUrl || ''}
          onChange={onChangeInput}
        />
      </div>

      <div className="profile__input mb-20">
        <Input
          type="text"
          name="inviteCode"
          label="Invite code"
          placeholder="Код пригласившего"
          value={formData?.inviteCode || ''}
          onChange={onChangeInput}
        />
      </div>

      <div className="profile__input mb-20">
        <Input
          type="text"
          name="referralCode"
          label="Referral code"
          placeholder="Собственный код"
          value={formData?.referralCode || ''}
          onChange={onChangeInput}
        />
      </div>

      {renderCheckbox('isActivated', 'Is activated', true)}
      {renderCheckbox('isConfirmAgreement', 'Is confirm agreement', true)}
      {renderCheckbox('isFreeTrial', 'Is free trial')}
      {renderCheckbox('isGifted', 'Is gifted')}

      <div className="profile__input mb-20">
        <label className="dropdown-label">Referrals — id пользователей</label>
        <div className="profile__referrals">
          {referrals.length === 0 && (
            <Text as="span" className="profile__referrals-empty">Пусто</Text>
          )}
          {referrals.map((id) => (
            <span className="profile__referral" key={`referral-${id}`}>
              {id}
              <button type="button" onClick={() => removeReferral(id)}>×</button>
            </span>
          ))}
        </div>
        {/* Кнопка, а не вложенная форма: блок стоит внутри admin-user-info, а form в form
            браузер не допускает — Enter улетал бы в сохранение профиля. */}
        <div className="profile__referrals-add">
          <input id="profile-referral-input" type="number" placeholder="ID пользователя" />
          <button type="button" onClick={addReferral}>Добавить</button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePatreon;
